import { FormikErrors, FormikTouched } from 'formik';
import styled from 'styled-components';
import { LoadingIndicator } from '@spotify-internal/encore-web';
import { useT } from '@spotify-internal/i18n-core';
import { FETCH_STATUS } from '@hooks/useSWRFetch/types';
import { formBodyHeight } from './constants';
import { ErrorState } from './ErrorState';
import { RelocationForm } from './RelocationForm';
import { SuccessState } from './SuccessState';
import { FormValues } from './types';

const LoadingIndicatorContainer = styled.div`
  display: flex;
  min-height: ${formBodyHeight};
  justify-content: center;
  align-items: center;
`;

interface FormBodyProps {
  status: FETCH_STATUS;
  values: FormValues;
  handleChange: (e: React.ChangeEvent<any>) => void;
  handleBlur: (e: any) => void;
  errors: FormikErrors<FormValues>;
  touched: FormikTouched<FormValues>;
  disableEmailInput: boolean;
  firstBtnRef: React.MutableRefObject<HTMLElement | null>;
}

export const FormBody = ({
  status,
  values,
  handleChange,
  handleBlur,
  errors,
  touched,
  disableEmailInput,
  firstBtnRef,
}: FormBodyProps) => {
  const t = useT();

  const isLoading = status === FETCH_STATUS.FETCHING;
  const isError = status === FETCH_STATUS.ERROR;
  const isFetched = status === FETCH_STATUS.SUCCESS;

  if (isLoading) {
    return (
      <LoadingIndicatorContainer>
        <LoadingIndicator />
      </LoadingIndicatorContainer>
    );
  }
  if (!isLoading && isError) {
    return (
      <ErrorState
        title={t(
          'atlas.errors.error_submitting',
          'Error submitting',
          'Error state after submitting form',
        )}
        text={t(
          'atlas.errors.error_submitting_try_again',
          'There was a problem submitting the form. Please try submitting again or reach out to <a href="mailto:content-ops+ingestion@spotify.com">Content Ops</a>.',
          '"Content Ops" should not be translated and should be kept in English because it refers to a name of a Spotify team.',
        )}
        textHasLink
      />
    );
  }
  if (!isLoading && isFetched) {
    return (
      <SuccessState
        title={t(
          'atlas.success.form_submitted',
          'Form submitted',
          'Confirmation that form was succesfully submitted',
        )}
        text={t(
          'atlas.success.thanks_for_reaching_out_watch_inbox',
          'Thanks for reaching out. Watch your inbox for a response from us.',
          'Indicates to user that we will reach out to them via email',
        )}
      />
    );
  }
  return (
    <RelocationForm
      firstBtnRef={firstBtnRef}
      values={values}
      handleChange={handleChange}
      handleBlur={handleBlur}
      errors={errors}
      touched={touched}
      disableEmailInput={disableEmailInput}
    />
  );
};
